import React, { useEffect, useRef, useState } from "react";
import { changeProduction, listProductions, loadComicPage, loadProduction, quoteProduction } from "./api";
import type { ProductionRun, StudioBootstrap } from "./types";

const active = (run: ProductionRun) => !["completed", "stopped", "awaiting_confirmation"].includes(run.status);

export function ProductionPanel({ studio, pageId, flushSaves, onUpdate, onReview, onOpenPage, onOpenPanel }: {
  studio: StudioBootstrap;
  pageId: string;
  flushSaves: () => Promise<void>;
  onUpdate: (run: ProductionRun) => Promise<void>;
  onReview: (run: ProductionRun) => void;
  onOpenPage: (pageId: string) => void;
  onOpenPanel: (pageId: string, panelId: string) => Promise<void>;
}) {
  const [run, setRun] = useState<ProductionRun | null>(null);
  const [story, setStory] = useState(studio.project_data?.story_source.text ?? "");
  const [pages, setPages] = useState(Math.min(30, Math.max(1, studio.project_data?.story_source.expected_pages ?? 1)));
  const [renderPages, setRenderPages] = useState(1);
  const [layout, setLayout] = useState(studio.active_page?.layout_key ?? "four_grid");
  const [reviewStoryboard, setReviewStoryboard] = useState(false);
  const [reviewCharacter, setReviewCharacter] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [panelOpenKey, setPanelOpenKey] = useState("");
  const [panelOpenError, setPanelOpenError] = useState("");
  const [budget, setBudget] = useState(0);
  const [showCompletedDetails, setShowCompletedDetails] = useState(false);
  const dismissed = useRef(new Set<string>());
  const requestRef = useRef({ key: "", id: "" });
  const runRef = useRef(run);
  const studioRef = useRef(studio);
  const onUpdateRef = useRef(onUpdate);
  runRef.current = run;
  studioRef.current = studio;
  onUpdateRef.current = onUpdate;
  const projectId = studio.active_project?.id;

  const receive = async (value: ProductionRun) => {
    runRef.current = value;
    setRun(value);
    setBudget(value.budget_limit);
    const url = new URL(window.location.href);
    url.searchParams.set("production_id", value.id);
    window.history.replaceState({}, "", url);
    await onUpdateRef.current(value);
  };

  useEffect(() => {
    if (!projectId || !studio.urls.production_list_template) return;
    let cancelled = false;
    let checking = false;
    let timer: number | undefined;
    const check = async () => {
      if (checking) return;
      checking = true;
      window.clearTimeout(timer);
      try {
        const current = runRef.current;
        const requested = new URL(window.location.href).searchParams.get("production_id");
        const value = current ? await loadProduction(studioRef.current, current.id)
          : requested ? await loadProduction(studioRef.current, requested)
            : await listProductions(studioRef.current).then((items) => {
              const visible = items.filter((item) => !dismissed.current.has(item.id));
              return visible.find(active) ?? visible[0] ?? null;
            });
        if (cancelled) return;
        if (value) {
          if (value.project_id !== projectId) throw new Error("現在の作品の制作状況を開いてください。");
          setRun(value);
          runRef.current = value;
          setBudget((current) => current || value.budget_limit);
          await onUpdateRef.current(value);
        }
        setError("");
      } catch (reason) {
        if (!cancelled) setError(reason instanceof Error ? `${reason.message} 制作はサーバーで続行されます。` : "接続を確認できません。制作状況を再確認します。");
      } finally {
        checking = false;
        if (!cancelled && (!runRef.current || !["completed", "stopped"].includes(runRef.current.status))) timer = window.setTimeout(check, 4000);
      }
    };
    void check();
    const reconnect = () => void check();
    window.addEventListener("online", reconnect);
    window.addEventListener("pageshow", reconnect);
    return () => { cancelled = true; window.clearTimeout(timer); window.removeEventListener("online", reconnect); window.removeEventListener("pageshow", reconnect); };
  }, [projectId, studio.urls.production_list_template, run?.id, run?.status]);

  useEffect(() => {
    const open = () => {
      setShowCompletedDetails(true);
      document.getElementById("comic-production")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const action = (event: Event) => {
      if ((event as CustomEvent).detail?.action === "production") open();
    };
    const recover = (event: Event) => {
      const runId = (event as CustomEvent<{ runId?: string }>).detail?.runId;
      if (!runId) return;
      void loadProduction(studioRef.current, runId).then(async (original) => {
        if (original.project_id !== studioRef.current.active_project?.id) return;
        await receive(original);
        open();
      }).catch((reason) => setError(reason instanceof Error ? reason.message : "元の制作状況を開けませんでした。"));
    };
    window.addEventListener("comic:editor-action", action);
    window.addEventListener("comic:production-open-requested", recover);
    return () => { window.removeEventListener("comic:editor-action", action); window.removeEventListener("comic:production-open-requested", recover); };
  }, []);

  const execute = async (work: () => Promise<ProductionRun>) => {
    if (busy) return;
    setBusy(true); setError("");
    try { await flushSaves(); await receive(await work()); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "操作を完了できませんでした。状況を確認してから再実行してください。"); }
    finally { setBusy(false); }
  };

  const quote = (targetPageId?: string) => execute(() => {
    const input = {
      story_text: story,
      story_total_pages: pages,
      preferred_layout_key: layout,
      polish_dialogue: false,
      review_storyboard: reviewStoryboard,
      review_character: reviewCharacter,
      ...(targetPageId ? { render_page_ids: [targetPageId] } : { render_page_range: [1, Math.min(renderPages, pages)] }),
    };
    const key = JSON.stringify(input);
    if (requestRef.current.key !== key) requestRef.current = { key, id: crypto.randomUUID() };
    return quoteProduction(studioRef.current, { ...input, client_request_id: requestRef.current.id });
  });

  const allPages = studio.chapters.flatMap((chapter) => chapter.pages);
  const hasExistingPage = Boolean(studio.active_page?.editor_state.bubbles.length
    || studio.active_page?.panels.some((panel) => panel.current_asset_id || panel.content.story_goal
      || panel.content.characters.length || panel.content.dialogue.length || panel.content.narration.length));
  const lastPage = pageId;
  const lastIndex = allPages.findIndex((page) => page.id === lastPage);
  const nextPage = lastIndex >= 0 ? allPages[lastIndex + 1] : undefined;
  const running = run && ["ready", "running", "stopping"].includes(run.status);
  const done = run?.steps.filter((step) => ["completed", "success", "reused", "skipped", "applied"].includes(step.status)).length ?? 0;
  const review = run && (["review_storyboard", "review_character", "character_ambiguous"].includes(run.pause_reason)
    || (run.pause_reason === "input_changed" && run.source_job_id && !run.steps.some((step) => step.target === "panel")));
  const targetPanels = run?.status === "paused" && ["input_changed", "generation_failed"].includes(run.pause_reason)
    ? run.steps.reduce<Array<{ pageId: string; panelId: string; stepOrder: number }>>((items, step, index) => {
      if (!step.page_id || !step.panel_id) return items;
      const key = `${step.page_id}:${step.panel_id}`;
      if (items.some((item) => `${item.pageId}:${item.panelId}` === key)) return items;
      return [...items, { pageId: step.page_id, panelId: step.panel_id, stepOrder: index + 1 }];
    }, [])
    : [];
  const targetPanelGroups = targetPanels.reduce<Array<{ pageId: string; label: string; items: typeof targetPanels }>>((groups, item) => {
    const page = allPages.find((candidate) => candidate.id === item.pageId);
    const current = groups.find((group) => group.pageId === item.pageId);
    if (current) {
      current.items.push(item);
      return groups;
    }
    return [...groups, { pageId: item.pageId, label: page ? `ページ${page.order}` : "対象ページ", items: [item] }];
  }, []);
  const openTargetPanel = async (target: { pageId: string; panelId: string }) => {
    const key = `${target.pageId}:${target.panelId}`;
    if (panelOpenKey) return;
    setPanelOpenKey(key);
    setPanelOpenError("");
    try {
      await onOpenPanel(target.pageId, target.panelId);
    } catch (reason) {
      setPanelOpenError(reason instanceof Error ? reason.message : "対象コマを開けませんでした。保存状態を確認してから再試行してください。");
    } finally {
      setPanelOpenKey("");
    }
  };

  const completed = run?.status === "completed";
  return <section id="comic-production" className={`comic-production${completed ? " comic-production--completed" : ""}`} aria-label="漫画の制作">
    {completed ? <header className="comic-production-completed">
      <div><strong>制作完了</strong><span>使用済み {run.spent}クレジット</span></div>
      <div className="comic-production-actions">
        {nextPage ? <button type="button" disabled={busy} onClick={() => void execute(async () => {
          const next = await loadComicPage(studioRef.current, nextPage.id);
          onOpenPage(nextPage.id);
          if (next.panels.length && next.panels.every((panel) => panel.current_asset_id)) return run;
          return quoteProduction(studioRef.current, { client_request_id: crypto.randomUUID(), render_page_ids: [nextPage.id], story_total_pages: pages, preferred_layout_key: next.layout_key });
        })}>次のページへ</button> : <span>本篇の最後のページです。</span>}
        <button type="button" aria-expanded={showCompletedDetails} aria-controls="comic-production-details" onClick={() => setShowCompletedDetails((value) => !value)}>
          {showCompletedDetails ? "詳細を閉じる" : "制作の詳細"}
        </button>
      </div>
    </header> : <header><h3>漫画を作る</h3><span>最初は1ページ。続きは読んでから。</span></header>}
    {(!run || ["completed", "stopped"].includes(run.status)) && <details open={!run && !hasExistingPage} className="comic-production-input">
      <summary>{run || hasExistingPage ? "このページの未完成部分を作る" : "物語・脚本"}</summary>
      {run || hasExistingPage ? <p>保存済みの内容で、画像のないコマだけを制作します。新しい話は編集ツールのネーム作成から追加できます。</p>
        : <label>どんな話にしますか<textarea rows={4} maxLength={20000} value={story} onChange={(event) => setStory(event.target.value)} placeholder="誰が、どこで、何をする話か教えてください。" /></label>}
      {!hasExistingPage && !run && <details><summary>ページ数・レイアウト・確認方法</summary>
        <label>物語全体のページ数<input type="number" min={1} max={30} value={pages} onChange={(event) => setPages(Math.min(30, Math.max(1, Math.floor(Number(event.target.value) || 1))))} /></label>
        <label>今回画像を作るページ数<input type="number" min={1} max={pages} value={Math.min(renderPages, pages)} onChange={(event) => setRenderPages(Math.min(pages, Math.max(1, Math.floor(Number(event.target.value) || 1))))} />先頭から制作します。続きは後から作れます。</label>
        <label>コマ割り<select value={layout} onChange={(event) => setLayout(event.target.value)}>{studio.layout_catalog.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}</select></label>
        <label><input type="checkbox" checked={reviewStoryboard} onChange={(event) => setReviewStoryboard(event.target.checked)} />先にネームを確認する</label>
        <label><input type="checkbox" checked={reviewCharacter} onChange={(event) => setReviewCharacter(event.target.checked)} />先に人物の提案を確認する</label>
      </details>}
      <button type="button" className="comic-button comic-button--primary" disabled={busy} onClick={() => void quote(allPages.length > 1 ? pageId : undefined)}>漫画初稿を生成</button>
    </details>}
    {run?.status === "awaiting_confirmation" && <div className="comic-production-quote">
      <p>物語全体：{run.story_total_pages}ページ ／ 今回：{run.approved_plan.render_selection_mode === "range" && Array.isArray(run.approved_plan.render_page_range)
        ? Number(run.approved_plan.render_page_range[1]) - Number(run.approved_plan.render_page_range[0]) + 1 : run.render_page_ids.length || 1}ページ</p>
      {typeof run.approved_plan.storyboard_estimate === "number" && typeof run.approved_plan.image_estimate === "number" && <p>ネーム：{run.approved_plan.storyboard_estimate === 0 ? "保存済みを使用" : `${run.approved_plan.storyboard_estimate}クレジット`} ／ 不足画像：上限{run.approved_plan.image_estimate}クレジット。文字配置の追加料金はありません。</p>}
      <p>今回の上限 <strong>{run.budget_limit}クレジット</strong>。確認後、ネーム・画像・文字配置を続けて制作します。</p>
      <button type="button" className="comic-button comic-button--primary" disabled={busy} onClick={() => void execute(() => changeProduction(studioRef.current, run, "start"))}>この範囲と費用で制作する</button>
      <button type="button" className="comic-button comic-button--quiet" disabled={busy} onClick={() => { dismissed.current.add(run.id); runRef.current = null; setRun(null); requestRef.current = { key: "", id: "" }; const url = new URL(window.location.href); url.searchParams.delete("production_id"); window.history.replaceState({}, "", url); }}>内容を見直す</button>
    </div>}
    {run && run.status !== "awaiting_confirmation" && (!completed || showCompletedDetails) && <div id="comic-production-details" aria-live="polite">
      <p>{run.message || (running ? "漫画を制作しています。画面を閉じても制作は続きます。" : run.readable ? "初稿を読めます。気になるコマを押して直せます。" : "制作状況を確認してください。")}</p>
      {run.steps.length > 0 && <progress value={done} max={run.steps.length} aria-label="制作の進捗" />}
      <p className="comic-production-fees">使用済み {run.spent} ／ 確保中 {run.reserved} ／ 上限 {run.budget_limit}クレジット</p>
      {targetPanelGroups.length > 0 && <section className="comic-production-panel-targets" aria-label="確認が必要なコマ">
        <p>対象ページのコマと生成候補を確認してから、制作を続けてください。</p>
        {targetPanelGroups.map((group) => <div key={group.pageId}>
          <strong>{group.label}</strong>
          {group.items.map((item) => {
            const key = `${item.pageId}:${item.panelId}`;
            return <div key={key}>
              <span>手順{item.stepOrder}</span>
              <button
                type="button"
                className="comic-button comic-button--quiet"
                disabled={Boolean(panelOpenKey) || busy}
                aria-label={`${group.label}・手順${item.stepOrder}の対象コマと候補を確認`}
                onClick={() => void openTargetPanel(item)}
              >対象コマ・候補を確認</button>
            </div>;
          })}
        </div>)}
        {panelOpenError && <p className="comic-form-error" role="alert">{panelOpenError}</p>}
      </section>}
      <div className="comic-production-actions">
        {(running || run.status === "paused") && <button type="button" disabled={busy || run.status === "stopping"} onClick={() => void execute(() => changeProduction(studioRef.current, run, "stop"))}>{run.status === "stopping" ? "実行中の結果を保存して停止します…" : "この後の制作を停止"}</button>}
        {review && <button type="button" disabled={busy} onClick={() => onReview(run)}>下書き・人物を確認</button>}
        {run.pause_reason === "budget_changed" && <label>確認する費用上限<input type="number" min={run.spent + run.reserved} value={budget} onChange={(event) => setBudget(Math.max(run.spent + run.reserved, Number(event.target.value) || 0))} /></label>}
        {["paused", "stopped"].includes(run.status) && !review && run.pause_reason !== "provider_unknown" && <button type="button" disabled={busy} onClick={() => void execute(() => changeProduction(studioRef.current, run, "resume", {
          ...(run.pause_reason === "generation_failed" ? { retry_failed: true } : {}),
          ...(run.pause_reason === "budget_changed" ? { budget_limit: budget } : {}),
        }))}>{run.pause_reason === "generation_failed" ? "上限内で未完了分を再試行" : run.pause_reason === "budget_changed" ? "この上限を確認して続ける" : "未完了分を続ける"}</button>}
        {run.pause_reason === "provider_unknown" && <p>送信結果を確認中です。追加の生成は行わず、確認結果をお待ちください。</p>}
      </div>
    </div>}
    {error && <p className="comic-form-error" role="alert">{error}</p>}
    {busy && <p role="status">確認しています…</p>}
  </section>;
}
